/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  DismissJobNotificationsData,
  DismissJobNotificationsResponse,
  DismissJobNotificationsResponse2,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type DismissJobNotificationsMutationConfig = {
  onSuccess?: (data: DismissJobNotificationsResponse, variables: Pick<DismissJobNotificationsData, 'status'>) => void;
  onError?: (error: unknown, variables: Pick<DismissJobNotificationsData, 'status'>) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for dismissing all job notifications of a specific type.
 *
 * @param config.onSuccess - Called after successful dismissal
 * @param config.onError - Called if dismissal fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for dismissing job notifications
 *
 * @example
 * ```tsx
 * const { mutate: dismissNotifications } = useDismissJobNotificationsMutation({
 *   onSuccess: (data) => console.log('Dismissed:', data.data.totalDismissed),
 *   onError: (error) => console.error('Dismissal failed:', error)
 * });
 *
 * // Dismiss all warning notifications (tenantId is added automatically from store)
 * dismissNotifications({ status: 'warning' });
 *
 * ```
 */
export function useDismissJobNotificationsMutation({
  onSuccess,
  onError,
  onSettled,
}: DismissJobNotificationsMutationConfig = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  return useMutation<DismissJobNotificationsResponse2, unknown, Pick<DismissJobNotificationsData, 'status'>>({
    mutationFn: (payload: Pick<DismissJobNotificationsData, 'status'>) => {
      return mgmtPlaneAPIClient.jobs.dismissJobNotifications({
        tenantId: selectedTenantId!,
        status: payload.status,
      });
    },
    onSuccess: (data, variables) => {
      // Invalidate job notifications query to refresh counts
      queryClient.invalidateQueries({ queryKey: ['jobNotifications'] });
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
  });
}
